import { Component, output, input, OnInit, OnDestroy, signal, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';

/**
 * Voice Recording UI Component
 * Displays a waveform animation, timer, and controls for voice recording
 * Provides visual feedback during active recording sessions
 */
@Component({
    selector: 'app-voice-recording-ui',
    standalone: true,
    imports: [CommonModule, ButtonComponent],
    templateUrl: './voice-recording-ui.component.html',
    styleUrls: ['./voice-recording-ui.component.scss']
})
export class VoiceRecordingUIComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('waveformContainer') waveformContainer!: ElementRef<HTMLDivElement>;

    // Output events
    cancel = output<void>();
    confirm = output<void>();

    // Input for audio level (0-100) to drive waveform animation
    audioLevel = input<number>(0);

    // Recording state
    elapsedTime = signal<string>('0:00');
    waveformBars = signal<number[]>([]);

    private startTime: number = 0;
    private timerInterval: any;
    private animationInterval: any;
    private resizeObserver: ResizeObserver | null = null;

    ngOnInit(): void {
        this.startRecording();
    }

    ngAfterViewInit() {
        this.setupResizeObserver();
    }

    ngOnDestroy(): void {
        this.stopRecording();
        this.resizeObserver?.disconnect();
    }

    private setupResizeObserver() {
        if (!this.waveformContainer) return;

        this.resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                this.updateBarCount(entry.contentRect.width);
            }
        });

        this.resizeObserver.observe(this.waveformContainer.nativeElement);
    }

    private updateBarCount(width: number) {
        // Each bar is 3px width + 2px gap = 5px total
        const barWidth = 5;
        // Subtract some padding to be safe
        const count = Math.floor((width - 10) / barWidth);

        const current = this.waveformBars();

        if (count > current.length) {
            // Need more bars: fill with base height (calm)
            const filler = Array(count - current.length).fill(15);
            this.waveformBars.set([...filler, ...current]);
        } else if (count < current.length) {
            // Need fewer bars: trim from the start (oldest data)
            this.waveformBars.set(current.slice(current.length - count));
        }
    }

    /**
     * Starts the recording timer and waveform animation
     */
    private startRecording(): void {
        this.startTime = Date.now();

        // Initialize waveform bars (40 bars for the visualization)
        this.initializeWaveform();

        // Update timer every 100ms for smooth display
        this.timerInterval = setInterval(() => {
            this.updateTimer();
        }, 100);

        // Animate waveform bars
        this.animateWaveform();
    }

    /**
     * Stops the recording timer and animation
     */
    private stopRecording(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        if (this.animationInterval) {
            clearInterval(this.animationInterval);
        }
    }

    /**
     * Updates the elapsed time display
     */
    private updateTimer(): void {
        const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        this.elapsedTime.set(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }

    /**
     * Initializes the waveform bars array
     */
    private initializeWaveform(): void {
        // Start with a safe default, ResizeObserver will correct it immediately
        const bars: number[] = Array(40).fill(15);
        this.waveformBars.set(bars);
    }

    /**
     * Animates the waveform by updating bar heights
     */
    private animateWaveform(): void {
        this.animationInterval = setInterval(() => {
            const currentBars = this.waveformBars();
            // Shift bars to the left and add new bar on the right
            const newBars = [...currentBars.slice(1), this.calculateBarHeight()];
            this.waveformBars.set(newBars);
        }, 100);
    }

    /**
     * Calculates bar height based on audio level
     * Combines the actual audio signal with some randomness for a natural waveform look
     */
    private calculateBarHeight(): number {
        const level = this.audioLevel();

        // Base height ensures bars are always visible even when silent
        const baseHeight = 15;

        // Calculate height based on audio level (max 70% influence)
        const levelComponent = (level / 100) * 70;

        // Add randomness for waveform texture (more variation at higher volumes)
        // If silent (level < 5), minimal variation to look calm
        const variance = level > 5 ? 20 : 5;
        const randomComponent = Math.random() * variance;

        return Math.min(100, Math.max(baseHeight, baseHeight + levelComponent + randomComponent));
    }

    /**
     * Generates a random height for waveform bars
     * Heights vary to create a natural waveform effect
     */
    private getRandomHeight(): number {
        return this.calculateBarHeight();
    }

    /**
     * Handles cancel button click
     * Emits cancel event to parent component
     */
    onCancel(): void {
        this.cancel.emit();
    }

    /**
     * Handles confirm button click
     * Emits confirm event to parent component
     */
    onConfirm(): void {
        this.confirm.emit();
    }
}

